import { Check, X } from 'lucide-react';
import styled from 'styled-components';

import { Button } from '../../../components/atoms/button/button';
import { Drawer } from '../../../components/molecules/drawer/drawer';
import { ErrorState } from '../../../components/molecules/error-state/error-state';
import { Spinner } from '../../../components/atoms/spinner/spinner';
import {
  isDecidivel,
  MoedaSaque,
  SaqueContaDetail,
  SaqueDetail,
  TIPO_CHAVE_PIX_LABEL,
  TIPO_CONTA_LABEL,
  TipoChavePixSaque,
  TipoContaSaque,
} from '../../../types/saque';
import { formatPais } from '../../../utils/pais';
import { formatDateTime, formatMoney } from './format';
import { SaqueStatusBadge } from './saque-status-badge';

interface Props {
  isOpen: boolean;
  detail: SaqueDetail | null;
  status: 'idle' | 'loading' | 'error';
  errorMessage?: string | null;
  onClose: () => void;
  onConfirm: () => void;
  onRefuse: () => void;
}

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }

  h3 {
    margin: 0 0 4px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;

  span:first-child {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  span:last-child {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-weight: 500;
    text-align: right;
    word-break: break-word;
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 8px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 60px 0;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Money = styled.div`
  font-variant-numeric: tabular-nums;
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ButtonsHelp = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: right;
  flex: 1;
`;

const AccountKind = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.bgMuted};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const isBrlAccount = (conta: SaqueContaDetail): boolean => {
  if (conta.moeda != null) return conta.moeda === MoedaSaque.BRL;
  return Boolean(
    conta.bancoCodigo || conta.agencia || conta.contaNumero || conta.chavePix,
  );
};

const renderEndereco = (endereco: SaqueContaDetail['endereco']) => {
  if (!endereco) return null;
  const linha1 = [endereco.cidade, endereco.estado]
    .filter(Boolean)
    .join(', ');
  const linha2 = [endereco.pais, endereco.codigoPostal]
    .filter(Boolean)
    .join(' · ');
  return [linha1, linha2, endereco.complemento]
    .filter(Boolean)
    .join(' — ');
};

const ContaBrl = ({ conta }: { conta: SaqueContaDetail }) => (
  <>
    {conta.pais && (
      <Row>
        <span>País</span>
        <span>{formatPais(conta.pais)}</span>
      </Row>
    )}
    {conta.bancoNome && (
      <Row>
        <span>Nome do banco</span>
        <span>{conta.bancoNome}</span>
      </Row>
    )}
    {conta.nome && (
      <Row>
        <span>Banco</span>
        <span>
          {conta.bancoCodigo ? `${conta.bancoCodigo} · ` : ''}
          {conta.nome}
        </span>
      </Row>
    )}
    {conta.agencia && (
      <Row>
        <span>Agência</span>
        <span>{conta.agencia}</span>
      </Row>
    )}
    {conta.contaNumero && (
      <Row>
        <span>Conta</span>
        <span>
          {conta.contaNumero}
          {conta.contaDigito ? `-${conta.contaDigito}` : ''}
        </span>
      </Row>
    )}
    {conta.tipoConta != null && (
      <Row>
        <span>Tipo de conta</span>
        <span>
          {TIPO_CONTA_LABEL[conta.tipoConta as TipoContaSaque] ?? '—'}
        </span>
      </Row>
    )}
    {conta.chavePix && (
      <Row>
        <span>
          Chave PIX
          {conta.tipoChavePix != null
            ? ` (${TIPO_CHAVE_PIX_LABEL[conta.tipoChavePix as TipoChavePixSaque] ?? '—'})`
            : ''}
        </span>
        <span>{conta.chavePix}</span>
      </Row>
    )}
    {!conta.bancoCodigo &&
      !conta.agencia &&
      !conta.contaNumero &&
      !conta.chavePix && (
        <Row>
          <span>—</span>
          <span>Sem dados bancários cadastrados</span>
        </Row>
      )}
  </>
);

const ContaInternacional = ({
  conta,
  moedaSigla,
}: {
  conta: SaqueContaDetail;
  moedaSigla: string;
}) => {
  const endereco = renderEndereco(conta.endereco);
  const pais = conta.pais ?? conta.endereco?.pais ?? null;
  return (
    <>
      {pais && (
        <Row>
          <span>País</span>
          <span>{formatPais(pais)}</span>
        </Row>
      )}
      {conta.nomeTitular && (
        <Row>
          <span>Titular</span>
          <span>{conta.nomeTitular}</span>
        </Row>
      )}
      {conta.bancoNome && (
        <Row>
          <span>Nome do banco</span>
          <span>{conta.bancoNome}</span>
        </Row>
      )}
      {conta.nome && (
        <Row>
          <span>Banco</span>
          <span>{conta.nome}</span>
        </Row>
      )}
      {conta.routingNumber && (
        <Row>
          <span>Routing / ABA</span>
          <span>{conta.routingNumber}</span>
        </Row>
      )}
      {conta.swiftCode && (
        <Row>
          <span>Código SWIFT / BIC</span>
          <span>{conta.swiftCode}</span>
        </Row>
      )}
      {conta.contaNumero && (
        <Row>
          <span>Conta</span>
          <span>
            {conta.contaNumero}
            {conta.contaDigito ? `-${conta.contaDigito}` : ''}
          </span>
        </Row>
      )}
      <Row>
        <span>Moeda da conta</span>
        <span>{moedaSigla}</span>
      </Row>
      {conta.emailBeneficiario && (
        <Row>
          <span>E-mail do beneficiário</span>
          <span>{conta.emailBeneficiario}</span>
        </Row>
      )}
      {endereco && (
        <Row>
          <span>Endereço</span>
          <span>{endereco}</span>
        </Row>
      )}
      {!conta.routingNumber &&
        !conta.swiftCode &&
        !conta.emailBeneficiario &&
        !conta.contaNumero && (
          <Row>
            <span>—</span>
            <span>Sem dados internacionais cadastrados</span>
          </Row>
        )}
    </>
  );
};

export const SaqueDetailDrawer = ({
  isOpen,
  detail,
  status,
  errorMessage,
  onClose,
  onConfirm,
  onRefuse,
}: Props) => {
  const decidivel = detail ? isDecidivel(detail.status) : false;
  const tooltip = !decidivel && detail
    ? `Saque em status "${detail.statusDescricao}" não pode ser alterado`
    : undefined;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Detalhes do saque"
      description={detail ? `${detail.cliente.nome} ${detail.cliente.sobrenome ?? ''}`.trim() : undefined}
      footer={
        detail ? (
          <>
            {!decidivel && tooltip && (
              <ButtonsHelp>{tooltip}</ButtonsHelp>
            )}
            <Button
              variant="secondary"
              onClick={onRefuse}
              disabled={!decidivel}
              title={tooltip}
              startIcon={<X size={14} />}
              data-testid="open-refuse-modal"
            >
              Recusar
            </Button>
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={!decidivel}
              title={tooltip}
              startIcon={<Check size={14} />}
              data-testid="open-confirm-modal"
            >
              Confirmar saque
            </Button>
          </>
        ) : null
      }
    >
      {status === 'loading' && (
        <Loading>
          <Spinner /> Carregando detalhes…
        </Loading>
      )}

      {status === 'error' && (
        <ErrorState
          title="Não foi possível carregar o saque"
          description={errorMessage ?? 'Tente novamente mais tarde.'}
        />
      )}

      {detail && status !== 'loading' && (
        <>
          <Section>
            <StatusRow>
              <Title>Saque #{detail.id.slice(0, 8)}</Title>
              <SaqueStatusBadge
                status={detail.status}
                description={detail.statusDescricao}
              />
            </StatusRow>
            <Money>{formatMoney(detail.valor, detail.moeda)}</Money>
            <Row>
              <span>Solicitado em</span>
              <span>{formatDateTime(detail.createdAt)}</span>
            </Row>
            <Row>
              <span>Moeda destino</span>
              <span>{detail.moedaDestinoSigla}</span>
            </Row>
            <Row>
              <span>Taxa</span>
              <span>{formatMoney(detail.taxa, detail.moeda)}</span>
            </Row>
            <Row>
              <span>Valor líquido</span>
              <span>
                {formatMoney(detail.valorLiquido, detail.moedaDestino)}
              </span>
            </Row>
          </Section>

          <Section>
            <h3>Cliente</h3>
            <Row>
              <span>Nome</span>
              <span>
                {detail.cliente.nome} {detail.cliente.sobrenome ?? ''}
              </span>
            </Row>
            <Row>
              <span>E-mail</span>
              <span>{detail.cliente.email}</span>
            </Row>
            {detail.cliente.documento && (
              <Row>
                <span>Documento</span>
                <span>{detail.cliente.documento}</span>
              </Row>
            )}
          </Section>

          <Section>
            <SectionHeader>
              <h3>Conta bancária</h3>
              {detail.contaBancaria.id && (
                <AccountKind>
                  {isBrlAccount(detail.contaBancaria)
                    ? 'BRL · PIX/TED'
                    : `${detail.contaBancaria.moedaSigla ?? 'Internacional'} · Wire`}
                </AccountKind>
              )}
            </SectionHeader>
            {detail.contaBancaria.id ? (
              isBrlAccount(detail.contaBancaria) ? (
                <ContaBrl conta={detail.contaBancaria} />
              ) : (
                <ContaInternacional
                  conta={detail.contaBancaria}
                  moedaSigla={
                    detail.contaBancaria.moedaSigla ?? detail.moedaDestinoSigla
                  }
                />
              )
            ) : (
              <Row>
                <span>—</span>
                <span>Sem conta vinculada</span>
              </Row>
            )}
          </Section>
        </>
      )}
    </Drawer>
  );
};
